import {
  PromotionEventModel,
  TrackableRecipe,
} from '@fdc/components/ui/nz/analytics-event/analytics-models';
import { CustomEventType } from '@fdc/constants/custom-event.constants';
import { trackAnalyticsEvent } from '@fdc/utils/analytics';
import { useDebounce } from '@woolworthslimited/core-components-react/shared/hooks/useDebounce';
import { useCallback, useEffect, useRef } from 'react';

export const RECIPE_TILE_VIEW_FLUSH_DEBOUNCE_MS = 400;

type DebouncedRecipeFlush = (() => void) & {
  cleanup?: () => void;
};

type UseRecipeTileListViewTrackingParams<
  T extends TrackableRecipe = TrackableRecipe,
> = {
  recipeList: T[];
  analyticsHeading?: string;
  analyticsSectionId?: string;
  analyticsOption?: string;
  flushDebounceMs?: number;
  onAnalyticsTrack?: (eventType: string, data: PromotionEventModel) => void;
};

export const useRecipeTileListViewTracking = <
  T extends TrackableRecipe = TrackableRecipe,
>({
  recipeList,
  analyticsHeading,
  analyticsSectionId,
  analyticsOption,
  flushDebounceMs,
  onAnalyticsTrack,
}: UseRecipeTileListViewTrackingParams<T>) => {
  const viewedRecipeKeysRef = useRef(new Set<string>());
  const pendingRecipeViewsRef = useRef<Array<{ recipe: T; index: number }>>([]);

  const flushPendingRecipeViews = useCallback(() => {
    if (pendingRecipeViewsRef.current.length === 0) {
      return;
    }

    const visibleRecipes = [...pendingRecipeViewsRef.current]
      .sort((left, right) => left.index - right.index)
      .map((view) => ({
        ...view.recipe,
        index: view.index + 1,
      }));

    pendingRecipeViewsRef.current = [];

    const analyticsPayload: PromotionEventModel = {
      heading: analyticsHeading || recipeList[0]?.slug || 'N/A',
      sectionId: analyticsSectionId || 'N/A',
      option: analyticsOption || 'LATEST',
      recipes: visibleRecipes,
    };

    if (onAnalyticsTrack) {
      onAnalyticsTrack(
        CustomEventType.RECIPE_TILE_FIFTY_PERCENT_VIEW_EVENT,
        analyticsPayload
      );
      return;
    }

    trackAnalyticsEvent(
      CustomEventType.RECIPE_TILE_FIFTY_PERCENT_VIEW_EVENT,
      analyticsPayload
    );
  }, [
    analyticsHeading,
    analyticsOption,
    analyticsSectionId,
    onAnalyticsTrack,
    recipeList,
  ]);

  const debouncedFlushPendingRecipeViews = useDebounce<DebouncedRecipeFlush>(
    flushPendingRecipeViews,
    flushDebounceMs ?? RECIPE_TILE_VIEW_FLUSH_DEBOUNCE_MS
  );

  const queueRecipeView = useCallback(
    (recipe: T, index: number) => {
      const recipeKey = recipe.recipeId || recipe.slug || `${index}`;
      if (viewedRecipeKeysRef.current.has(recipeKey)) {
        return;
      }

      viewedRecipeKeysRef.current.add(recipeKey);
      pendingRecipeViewsRef.current.push({ recipe, index });
      debouncedFlushPendingRecipeViews();
    },
    [debouncedFlushPendingRecipeViews]
  );

  const recipeListKey = recipeList
    .map((recipe, index) => recipe.recipeId || recipe.slug || `${index}`)
    .join('|');

  useEffect(() => {
    viewedRecipeKeysRef.current.clear();
    pendingRecipeViewsRef.current = [];
    debouncedFlushPendingRecipeViews.cleanup?.();
  }, [debouncedFlushPendingRecipeViews, recipeListKey]);

  useEffect(() => {
    return () => {
      debouncedFlushPendingRecipeViews.cleanup?.();
    };
  }, [debouncedFlushPendingRecipeViews]);

  return { queueRecipeView };
};
